import React, { useEffect, useRef } from "react";
import "./AdSlot.css";

// Set this in .env once your Google AdSense account is approved:
// REACT_APP_ADSENSE_CLIENT_ID=ca-pub-XXXXXXXXXXXXXXXX
const AD_CLIENT = process.env.REACT_APP_ADSENSE_CLIENT_ID || "";

/**
 * Reusable ad placement used across the site.
 *
 * - While REACT_APP_ADSENSE_CLIENT_ID is unset (or the AdSense script hasn't
 *   loaded), this renders a labeled placeholder box so every ad position is
 *   visible in the layout during development.
 * - Once you set REACT_APP_ADSENSE_CLIENT_ID and uncomment the AdSense
 *   script tag in public/index.html, it renders a real <ins class="adsbygoogle">
 *   unit and asks AdSense to fill it - no other code changes needed.
 *
 * Props:
 *   slot    - your AdSense ad unit "slot" ID (from the AdSense dashboard)
 *   variant - "leaderboard" | "rectangle" | "in-feed" - controls sizing
 *   label   - placeholder text shown before AdSense is configured
 */
function AdSlot({ slot, variant = "leaderboard", label = "Advertisement" }) {
  const pushedRef = useRef(false);
  const hasAdSense = Boolean(AD_CLIENT) && typeof window !== "undefined" && Array.isArray(window.adsbygoogle);

  useEffect(() => {
    if (!hasAdSense || pushedRef.current) return;
    try {
      window.adsbygoogle.push({});
      pushedRef.current = true;
    } catch (err) {
      // AdSense script not ready yet - safe to ignore
    }
  }, [hasAdSense]);

  if (!hasAdSense) {
    return (
      <div className={`tt-adslot-placeholder tt-adslot-${variant}`} aria-hidden="true">
        <span>{label}</span>
      </div>
    );
  }

  return (
    <div className={`tt-adslot-wrap tt-adslot-${variant}`}>
      <ins
        className="adsbygoogle"
        style={{ display: "block", width: "100%", height: "100%" }}
        data-ad-client={AD_CLIENT}
        data-ad-slot={slot}
        data-ad-format="auto"
        data-full-width-responsive="true"
      />
    </div>
  );
}

export default AdSlot;
